import api from "./axios";
import { ClusterAnalysis } from "./patients";

// Результаты единого теста (sacca3.0) — GET /api/v3/scan/{scanId}/results

export type UnifiedBlockName = "animals" | "park" | "story";

export interface UnifiedBlockResult {
  association_id: string;
  wav_url: string | null;
  transcription: string | null;
  cluster_analysis: ClusterAnalysis | null;
  stt_language: string | null;
  stt_provider: string | null;
  processing_status: "processing" | "done" | "failed" | null;
  error: string | null;
  test_start: string | null;
  test_end: string | null;
  audio_index: number | null;
  transcription_edited_by: string | null;
  transcription_edited_at: string | null;
}

export interface UnifiedMstMetrics {
  total_trials: number;
  encode_trials: number;
  test_trials: number;
  timeout_count: number;
  old_accuracy: number;
  lure_similar_rate: number;
  lure_false_alarm_rate: number;
  new_correct_reject_rate: number;
  ldi: number;
  recognition_index: number;
  avg_rt_old_ms: number | null;
  avg_rt_lure_ms: number | null;
  avg_rt_new_ms: number | null;
  duration_sec: number | null;
}

export interface UnifiedMstResult {
  association_id: string;
  metrics: UnifiedMstMetrics | null;
  n_trials: number | null;
  trials_s3_key: string | null;
  started_at: string | null;
  finished_at: string | null;
  duration_sec: number | null;
}

// Ответ MCI-модели (CogNimbus, contracts/inference_response.json)

// more_info — данные панели «Tap for full report» (радар + SHAP-драйверы);
// присутствует, если артефакт модели обогащён more_info-агрегатами
export interface MciDomainPct {
  domain: string; // "Memory" | "Language & Semantic" | "Attention & Speed"
  pct: number;    // перцентиль vs healthy peers (выше = здоровее)
}

export interface MciDriver {
  construct: string; // клинический конструкт, напр. "Story recall"
  direction: "toward_impairment" | "protective";
  share: number;        // доля вклада конструкта в оценку пациента, %
  risk_points?: number; // абсолютный вклад в raw-риск (аудит, не для показа)
}

export interface MciMoreInfo {
  domain_profile: MciDomainPct[];
  ref_nc?: MciDomainPct[];  // границы зон радара — фиксированные, одинаковы для всех
  ref_mci?: MciDomainPct[];
  drivers: MciDriver[];
  driver_coverage: number;
  norm_reference?: string;
}

export interface MciResult {
  model_version: string;
  confidence: number;
  confidence_pct: number;
  zone: "rule_out" | "gray_zone" | "rule_in" | "severe";
  thresholds: { rule_out: number; rule_in: number; severe?: number };
  n_features_missing?: number;
  missing_features?: string[];
  ood?: { flag: boolean; n_features_out_of_range?: number; features?: any[] };
  mst_compliance?: { fast_response_rate: number; flag: boolean; reason: string | null } | null;
  more_info?: MciMoreInfo | null;
  disclaimer?: string;
}

export interface MciState {
  status: "processing" | "done" | "failed" | null;
  result: MciResult | null;
  scored_at: string | null;
  error: string | null;
  inputs_ready: boolean;
  enabled: boolean;
}

export interface ScanResults {
  scan: {
    id: string;
    patient_id: string | null;
    scan_part: string | null;
    language: string | null;
    scan_start: string | null;
    scan_end: string | null;
    display_mode: string | null;
    // веб-прохождение по ссылке
    administration?: "remote" | "supervised" | null;
    attempt_status?: "in_progress" | "completed" | "interrupted" | null;
    created_at: string | null;
    operator_email: string | null;
    operator_name: string | null;
    operator_role: string | null;
  };
  blocks: Partial<Record<UnifiedBlockName, UnifiedBlockResult>>;
  mst: UnifiedMstResult | null;
  mci: MciState;
}

export async function getScanResults(scanId: string): Promise<ScanResults> {
  const res = await api.get(`/v3/scan/${scanId}/results`);
  return res.data;
}

export async function updateScanAudioTranscription(
  associationId: string,
  transcription: string
): Promise<{ transcription: string; cluster_analysis: ClusterAnalysis | null }> {
  const res = await api.patch(`/v3/scan-audio/${associationId}`, { transcription });
  return res.data;
}

export async function reprocessScanAudio(associationId: string): Promise<void> {
  await api.post(`/v3/scan-audio/${associationId}/reprocess`);
}

export async function rescoreMci(scanId: string): Promise<void> {
  await api.post(`/v3/scan/${scanId}/mci-rescore`);
}
